import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { Observable, Subject, Subscription, catchError, combineLatest, debounceTime, filter, map, of, startWith, switchMap, tap } from 'rxjs';

import { ApiParams, CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { copyTextToClipboard } from '../../shared/browser.utils';
import { Option } from '../../shared/models/option.model';
import { XferConflict, XferConflictLevel, XferConflictResolveMethod, XferConflictType, XferImport } from '../../shared/models/xfer.model';
import { replayValue } from '../../shared/observable.utils';
import { TranslationService } from '../../translation.service';

interface ConflictRow extends XferConflict {
  selected: boolean
}

interface ResolveOption {
  title: string
  description: string
  value: XferConflictResolveMethod
}

@Component({
  host: { 'test-id': 'conflict-files' },
  selector: 'conflict-files',
  templateUrl: './conflict-files.component.html',
  styleUrls: ['./conflict-files.component.scss'],
})

export class ConflictFilesComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() importId: string;
  @Input() projectId: string;

  afterId: string | null = null;
  allConflictsSelected = false;
  beforeId: string | null = null;
  conflictTypeOptions: Option[];
  conflicts: ConflictRow[];
  conflicts$: Observable<ConflictRow[]>;
  count = 0;
  displayedColumns: string[] = ['select', 'fileDestination', 'level', 'actions', 'actionError'];
  focusedConflict: ConflictRow;
  globalAction: string;
  import: XferImport;
  isLoading = true;
  pageSize = 25;
  readonly refresh$ = new Subject<void>();
  readonly filters = new FormGroup({
    uploadPath: new FormControl(''),
    level: new FormControl(''),
  });
  typeResolveOptions: Map<XferConflictType, ResolveOption[]>;
  shouldShowAllPaths = false;
  total = 0;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  conflictLevelEnum: typeof XferConflictLevel = XferConflictLevel;

  get selectedConflicts(): XferConflict[] {
    return (this.conflicts && this.conflicts.filter(conflict => conflict.selected)) || [];
  }

  get containerConflictSelected(): boolean {
    return this.selectedConflicts.find(conflict => conflict.level !== XferConflictLevel.File) !== undefined;
  }

  get selectedConflictsReady(): boolean {
    return this.conflicts?.some(conflict => conflict.action);
  }

  private subscriptions = {
    messages: Subscription.EMPTY,
    resolve: Subscription.EMPTY,
    resolveOptions: Subscription.EMPTY,
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private translate: TranslationService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.messages = combineLatest([
      this.translate.getBundle('common.labels'),
      this.translate.getBundle('conflictFiles'),
    ]).subscribe(([commonLabels, conflictFiles]) => {
      this.conflictTypeOptions = Object.values(XferConflictLevel).map(
        value => ({ label: conflictFiles.get(`conflictLevels.${value}`), value })
      );
      this.conflictTypeOptions.unshift({ label: commonLabels.get('none'), value: null });
    });
    this.subscriptions.resolveOptions = combineLatest([
      this.coreApi.xfer.conflicts.getResolveMethods(),
      this.translate.getBundle('conflictFiles'),
    ]).subscribe(([typeMethods, translations]) => {
      this.typeResolveOptions = new Map(
        Object.keys(typeMethods).map((conflictType: XferConflictType) => {
          const options = typeMethods[conflictType].map((method: XferConflictResolveMethod) => {
            return {
              title: translations.get(`resolveMethods.${method}`),
              description: translations.get(`resolveDescriptions.${method}`),
              value: method,
            };
          });
          return [conflictType, options];
        }));
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.conflicts$ = combineLatest([
      this.coreApi.xfer.imports.get(this.importId, { join: 'refs.storage' }),
      replayValue(this.filters).pipe(debounceTime(200), tap(() => this.paginator.firstPage())),
      this.sort.sortChange.pipe(
        startWith({ active: this.sort.active, direction: this.sort.direction } as Sort),
        tap(() => this.paginator.firstPage()),
      ),
      this.paginator.page.pipe(
        startWith({ pageIndex: this.paginator.pageIndex, pageSize: this.paginator.pageSize } as PageEvent),
        filter(page => {
          if (this.pageSize !== page.pageSize) {
            this.pageSize = page.pageSize;
            if (page.pageIndex > 0) {
              this.paginator.firstPage();
              return false;
            }
          }
          return true;
        }),
        map(paginator => {
          const pageParams: ApiParams = { limit: paginator.pageSize };
          if (paginator.pageIndex > 0) {
            if (paginator.pageIndex >= paginator.previousPageIndex) {
              pageParams.after_id = this.afterId;
            } else {
              pageParams.before_id = this.beforeId;
            }
          }
          return pageParams;
        })
      ),
      this.refresh$.pipe(startWith(undefined)),
    ]).
      pipe(
        switchMap(([xferImportData, filters, sort, pageParams]) => {
          this.import = xferImportData;
          this.isLoading = true;

          const queryParams = {
            sort: `${sort.active}:${sort.direction}`,
            ...pageParams,
          };

          const filterParams = [`source=${this.importId}`];

          if (filters.level?.length > 0) {
            filterParams.push(`level=${filters.level}`);
          }

          if (filters.uploadPath?.length > 0) {
            filterParams.push(`upload_path=~${filters.uploadPath}`);
          }

          queryParams.filter = filterParams;

          return this.coreApi.xfer.conflicts.get({ ...queryParams }).pipe(
            map(page => {
              this.count = page.count;
              this.total = page.total;
              this.beforeId = page.prev_before_id;
              this.afterId = page.next_after_id;
              this.allConflictsSelected = false;
              this.conflicts = page.results as ConflictRow[];
              this.isLoading = false;
              this.cdr.markForCheck();
              return this.conflicts;
            }),
            catchError(error => {
              this.isLoading = false;
              this.logger.error(error);
              this.notifications.error('projectImport.conflicts.error');
              this.cdr.markForCheck();
              return of([]);
            })
          );
        }),
        startWith([]),
      );
  }

  updateConflictActions(): void {
    this.selectedConflicts.forEach(conflict => conflict.action = this.globalAction);
  }

  updateGlobalControls(header: boolean): void {
    if (header === true) {
      if (this.selectedConflicts.length < this.conflicts.length) {
        this.conflicts.forEach(conflict => conflict.selected = true);
      } else {
        this.conflicts.forEach(conflict => conflict.selected = false);
      }
    }

    if (header === false) {
      if (this.selectedConflicts.length < this.conflicts.length) {
        this.allConflictsSelected = false;
      }
      if (this.selectedConflicts.length === this.conflicts.length) {
        this.allConflictsSelected = true;
      }
    }

    if (this.containerConflictSelected && this.globalAction === 'update') {
      this.globalAction = undefined;
    }

    if (this.selectedConflicts.some(conflict => conflict.action !== this.globalAction)) {
      this.globalAction = undefined;
    }
  }

  resolveConflicts(): void {
    this.isLoading = true;
    const conflictsWithAction = this.conflicts.filter(item => item.action);
    this.subscriptions.resolve = this.coreApi.xfer.conflicts.resolve({
      project: this.projectId,
      actions: conflictsWithAction.map(conflict => ({
        id: conflict._id,
        strategy: conflict.action,
      })),
    }).subscribe({
      next: () => {
        if (conflictsWithAction.find(c => c._id === this.focusedConflict?._id)) {
          this.focusedConflict = null;
        }
        this.refresh$.next();
      },
      error: (error) => {
        this.logger.error(error);
        this.notifications.error('projectImport.conflictsResolve.error');
      },
    });
  }

  copyPathToClipboard(conflict: XferConflict): void {
    copyTextToClipboard(`${this.import.storage.url}/${conflict.upload_metadata.storage_prefix}/${conflict.upload_metadata.storage_paths[0]}`);
  }

  trackById(index: number, conflict: XferConflict): string {
    return conflict._id;
  }

  openSearchForConflict(): void {
    const parts = [];
    for (const key in this.focusedConflict.fields) {
      parts.push(`${this.focusedConflict.level}.${key} = "${this.focusedConflict.fields[key]}"`);
    }
    const flyql = parts.join(' AND ');
    window.open(`${window.location.href.split('#')[0]}#/search/${this.focusedConflict.level}s?flyql=${encodeURIComponent(flyql)}`, '_blank');
  }

  getGlobalResolveOptions(): ResolveOption[] {
    if (this.selectedConflicts.length === 0) {
      return [];
    }
    const options = this.selectedConflicts.map(c => this.typeResolveOptions.get(c.type));
    return options.reduce((a, b) => a.filter(aa => b.filter(bb => aa.value === bb.value)));
  }

  isVisibleField(key: string): Boolean {
    return ['uid', 'label', 'name'].includes(key);
  }
}
